/**
 * One defect word spelled two ways (plan v21 B1, 2026-10-13): "Grit Blast" and "Gritblast", "Burrs" and "Burr",
 * "Lug Way Burrs" and "Lugway Burr". A plant's files spell one defect several ways, and each spelling saved as a word of
 * its own splits one defect into several categories. This finds the twin, so a reviewer binds every spelling to one
 * defect.
 *
 * The words are packloader.DefectWords' (platform-core/internal/packloader/defect_words.go): the runs of ASCII letters
 * and digits, only A to Z folded to lower case, every other character a separator. The plural is defectWordForm's.
 * Two guards keep two different words from merging: a label with a letter, digit or mark outside ASCII is never the
 * same as another (those characters would drop out of its words), and two runs that meet digit to digit keep a
 * boundary ("Code 1 2" is not "Code 12"). The pairs in tools/forge-brain/tests/fixtures/defect_spelling_vectors.json pin
 * the rule for every reader of it.
 */

/** A defect label as lower-case words: the runs of ASCII letters and digits; every other character separates. */
export function defectWords(label: string): string[] {
  const words: string[] = [];
  let cur = "";
  for (let i = 0; i < label.length; i++) {
    const c = label.charCodeAt(i);
    if ((c >= 97 && c <= 122) || (c >= 48 && c <= 57)) {
      cur += label[i];
    } else if (c >= 65 && c <= 90) {
      cur += String.fromCharCode(c + 32);
    } else {
      // Any other character, a code unit above 0x7f included, separates two words (Go reads it byte by byte).
      if (cur) words.push(cur);
      cur = "";
    }
  }
  if (cur) words.push(cur);
  return words;
}

/** w is k, or a regular plural of it: +s; +es after s, x, z, ch or sh; a y after a consonant as ies. */
export function defectWordForm(w: string, k: string): boolean {
  if (w === k || w === `${k}s`) return true;
  if (["s", "x", "z", "ch", "sh"].some((end) => k.endsWith(end)) && w === `${k}es`) return true;
  const n = k.length;
  return n >= 2 && k[n - 1] === "y" && !"aeiou".includes(k[n - 2]) && w === `${k.slice(0, -1)}ies`;
}

// A letter, digit or mark outside ASCII: it would drop out of the label's words (defectWords reads ASCII alone).
const NON_ASCII_WORD = /[\p{L}\p{N}\p{M}]/u;

function isDigit(ch: string): boolean {
  return ch >= "0" && ch <= "9";
}

/**
 * A label's spelling form: its words joined with nothing between them, except a "_" where two runs meet digit to digit.
 * null when the label has no word, or holds a letter, digit or mark outside ASCII: such a label is never the same as
 * another by spelling (its exact key still matches its own decision).
 */
export function spellingForm(label: string): string | null {
  for (const ch of label) {
    if (ch.charCodeAt(0) > 0x7f && NON_ASCII_WORD.test(ch)) return null;
  }
  const words = defectWords(label);
  if (words.length === 0) return null;
  let out = words[0];
  for (const w of words.slice(1)) {
    out += isDigit(out[out.length - 1]) && isDigit(w[0]) ? `_${w}` : w;
  }
  return out;
}

/** Whether two spelling forms (spellingForm) are one word: equal, or one a regular plural of the other. */
export function sameSpelling(x: string | null, y: string | null): boolean {
  if (x === null || y === null) return false;
  return defectWordForm(x, y) || defectWordForm(y, x);
}

/**
 * Whether two defect words are one word spelled two ways: their spelling forms are equal, or one is a regular plural of
 * the other (so only the last word's plural folds). Symmetric, and not transitive, so it finds a twin and never keys a
 * map. A bracketed qualifier stays a word ("Burrs (General)" is not "Burrs"): it can carry meaning.
 */
export function sameDefectWord(a: string, b: string): boolean {
  return sameSpelling(spellingForm(a), spellingForm(b));
}
